'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, MessageCircle, UserPlus, Bookmark, X, Check } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';


interface Notification {
  id: string;
  type: 'comment_reply' | 'new_follower' | 'outfit_save';
  read: boolean;
  created_at: string;
  actor_name: string;
  actor_id?: string;
  outfit_id?: string;
  outfit_title?: string;
  comment_id?: string;
  message: string;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const ICON_MAP = {
  comment_reply: { Icon: MessageCircle, color: '#C9A96E', bg: 'rgba(201,169,110,0.12)' },
  new_follower: { Icon: UserPlus, color: '#7EB8A4', bg: 'rgba(126,184,164,0.12)' },
  outfit_save: { Icon: Bookmark, color: '#B07CC6', bg: 'rgba(176,124,198,0.12)' },
};

export default function NotificationBell() {
  const { user } = useAuth();
  const supabase = createClient();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(30);

      if (!error && data) {
        setNotifications(data as Notification[]);
      }
    } catch {
      // silently fail — table may not exist yet
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    if (open && user) fetchNotifications();
  }, [open, user, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const markAllRead = async () => {
    if (!user || notifications.every(n => n.read)) return;
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try {
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);
    } catch {
      // silently fail
    }
  };

  const markRead = async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    try {
      await supabase.from('notifications').update({ read: true }).eq('id', id);
    } catch {
      // silently fail
    }
  };

  function getActionLink(n: Notification): string {
    if (n.type === 'comment_reply' && n.outfit_id) return `/public-feed#outfit-${n.outfit_id}`;
    if (n.type === 'new_follower' && n.actor_id) return `/creator/${n.actor_id}`;
    if (n.type === 'outfit_save' && n.outfit_id) return `/public-feed#outfit-${n.outfit_id}`;
    return '/public-feed';
  }

  if (!user) return null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setOpen(prev => !prev)}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        className="relative flex items-center justify-center w-8 h-8 rounded-full transition-all duration-150 hover:opacity-70 active:scale-95"
        style={{ color: '#C9A96E' }}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center text-white font-bold rounded-full"
            style={{
              backgroundColor: '#C9A96E',
              fontSize: '9px',
              minWidth: '16px',
              height: '16px',
              padding: '0 3px',
              lineHeight: 1,
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={modalRef}
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 mt-2 rounded-2xl shadow-2xl overflow-hidden"
          style={{
            width: '360px',
            maxWidth: 'calc(100vw - 24px)',
            backgroundColor: '#FAF7F2',
            border: '1px solid hsl(35 20% 88%)',
            zIndex: 200,
            top: '100%',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: 'hsl(35 20% 88%)' }}
          >
            <div className="flex items-center gap-2">
              <Bell size={15} style={{ color: '#C9A96E' }} />
              <span className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <span
                  className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: 'rgba(201,169,110,0.15)', color: '#C9A96E' }}
                >
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-70"
                  style={{ color: '#C9A96E' }}
                  title="Mark all as read"
                >
                  <Check size={12} />
                  <span>All read</span>
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="flex items-center justify-center w-6 h-6 rounded-full transition-opacity hover:opacity-70"
                style={{ color: 'hsl(30 8% 52%)' }}
                aria-label="Close notifications"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="overflow-y-auto" style={{ maxHeight: '420px' }}>
            {loading ? (
              <div className="flex flex-col gap-3 p-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex gap-3 animate-pulse">
                    <div className="w-9 h-9 rounded-full flex-shrink-0" style={{ backgroundColor: 'hsl(35 15% 92%)' }} />
                    <div className="flex-1 space-y-2 pt-1">
                      <div className="h-3 rounded" style={{ backgroundColor: 'hsl(35 15% 92%)', width: '75%' }} />
                      <div className="h-2.5 rounded" style={{ backgroundColor: 'hsl(35 15% 92%)', width: '45%' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: 'rgba(201,169,110,0.1)' }}
                >
                  <Bell size={22} style={{ color: '#C9A96E', opacity: 0.6 }} />
                </div>
                <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>All caught up</p>
                <p className="text-xs mt-1" style={{ color: 'hsl(30 8% 52%)' }}>
                  New replies, followers, and saves will appear here
                </p>
              </div>
            ) : (
              <ul>
                {notifications.map((n, idx) => {
                  const { Icon, color, bg } = ICON_MAP[n.type];
                  const actionLink = getActionLink(n);
                  return (
                    <li
                      key={n.id}
                      className="relative"
                      style={{
                        borderBottom: idx < notifications.length - 1 ? '1px solid hsl(35 20% 92%)' : 'none',
                        backgroundColor: n.read ? 'transparent' : 'rgba(201,169,110,0.04)',
                      }}
                    >
                      <Link
                        href={actionLink}
                        onClick={() => { markRead(n.id); setOpen(false); }}
                        className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-black/[0.03] group"
                      >
                        {/* Icon */}
                        <div
                          className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center mt-0.5"
                          style={{ backgroundColor: bg }}
                        >
                          <Icon size={16} style={{ color }} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs leading-relaxed" style={{ color: '#1A1A1A' }}>
                            <span className="font-semibold">{n.actor_name}</span>{' '}
                            <span style={{ color: 'hsl(30 8% 42%)' }}>{n.message}</span>
                            {n.outfit_title && (
                              <span className="font-medium" style={{ color: '#C9A96E' }}>
                                {' '}"{n.outfit_title}"
                              </span>
                            )}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs" style={{ color: 'hsl(30 8% 62%)' }}>
                              {timeAgo(n.created_at)}
                            </span>
                            <span
                              className="text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ color: '#C9A96E' }}
                            >
                              View →
                            </span>
                          </div>
                        </div>

                        {/* Unread dot */}
                        {!n.read && (
                          <div
                            className="flex-shrink-0 w-2 h-2 rounded-full mt-2"
                            style={{ backgroundColor: '#C9A96E' }}
                          />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div
              className="px-4 py-2.5 border-t text-center"
              style={{ borderColor: 'hsl(35 20% 88%)' }}
            >
              <Link
                href="/public-feed"
                onClick={() => setOpen(false)}
                className="text-xs font-medium transition-opacity hover:opacity-70"
                style={{ color: '#C9A96E' }}
              >
                Go to Public Feed
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
